class CartDrawerUpsellVariantSelector extends HTMLElement {
  constructor() {
    super();
    
    // Only initialize if we're inside a cart drawer
    const cartDrawer = this.closest('cart-drawer');
    if (!cartDrawer) {
      // Not in a cart drawer, so don't initialize
      return;
    }
    
    // Get product data from data attribute
    this.product = JSON.parse(this.dataset.product || '{}');
    
    // Find the parent upsell item
    const upsellItem = this.closest('.product-upsell-item');
    if (!upsellItem) {
      return;
    }
    
    // Find the variant selectors within the upsell item
    this.variantSelects = upsellItem.querySelector('.product-upsell-item__variants');
    
    // Get the cart drawer elements
    this.cartDrawer = document.querySelector('cart-drawer');
    this.cartItems = this.cartDrawer?.querySelector('cart-drawer-items');
    
    // Get the form and id input
    this.form = this.querySelector('form');
    this.idInput = this.querySelector('[name="id"]');
    this.submitButton = this.querySelector('button[type="submit"]');
    
    // Get the variant data from the embedded JSON
    this.jsonData = this.querySelector('script[type="application/json"]');
    
    // Set up event listeners for variant selectors
    if (this.variantSelects) {
      // Handle dropdown selects
      this.selectElements = this.variantSelects.querySelectorAll('select');
      
      // Handle radio buttons for pills and swatches
      this.radioElements = this.variantSelects.querySelectorAll('input[type="radio"]');
      
      // Set up event listeners
      this.setupEventListeners();
    } else {
      console.log('No variant selectors found');
    }
    
    // Set up form submission
    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }
    
    // Store handle for cart operations
    this.handle = this.product.handle;
    
  }
  
  setupEventListeners() {
    // Single handler for all variant changes
    const handler = this.handleVariantChange.bind(this);
    
    // Set up dropdown event listeners
    if (this.selectElements) {
      this.selectElements.forEach(select => {
        select.addEventListener('change', handler);
      });
    }
    
    // Set up radio button event listeners
    if (this.radioElements && this.radioElements.length > 0) {
      this.radioElements.forEach(radio => {
        radio.addEventListener('change', handler);
        // Also add click listener as a backup
        radio.addEventListener('click', handler);
      });
    }
  }
  
  handleVariantChange() {
    const variant = this.getSelectedVariant();
    if (!variant) return;
    
    this.updateVariantId(variant.id);
    this.updateButtonState(variant.available);
    this.updateProductImage(variant.featured_image);
  }
  
  getSelectedVariant() {
    // Handle dropdown case
    if (this.selectElements && this.selectElements.length > 0) {
      const variantId = this.selectElements[0].value;
      if (variantId) {
        return this.getVariantData().find(v => 
          v.id.toString() === variantId.toString()
        );
      }
    }
    
    // Handle radio buttons case (pills and swatches)
    if (this.radioElements && this.radioElements.length > 0) {
      const checkedRadios = Array.from(this.radioElements).filter(radio => radio.checked);
      
      if (checkedRadios.length === 0) {
        console.log('No checked radio buttons found');
        return null;
      }
      
      // Build option values map
      const optionValues = {};
      checkedRadios.forEach(radio => {
        optionValues[radio.name] = radio.value;
      });
      
      // Find matching variant
      return this.getVariantData().find(variant => {
        return this.product.options.every((option, index) => {
          const selectedValue = optionValues[option];
          
          // If no value selected for this option, skip validation
          if (!selectedValue) {
            return true;
          }
          
          // Check if variant option matches selected value
          return variant.options[index] === selectedValue;
        });
      });
    }
    
    return null;
  }
  
  updateVariantId(variantId) {
    if (this.idInput) {
      this.idInput.value = variantId;
      this.dataset.id = variantId;
    }
  }
  
  updateButtonState(available) {
    if (!this.submitButton) return;
    
    if (available) {
      this.submitButton.removeAttribute('disabled');
    } else {
      this.submitButton.setAttribute('disabled', 'disabled');
    }
  }
  
  updateProductImage(featuredImage) {
    if (!featuredImage) return;
    
    const upsellItem = this.closest('.product-upsell-item');
    if (!upsellItem) return;
    
    const productImage = upsellItem.querySelector('.product-upsell-item__image img');
    if (productImage) {
      productImage.src = featuredImage.src;
    }
  }
  

  
  getVariantData() {
    if (!this.variantData && this.jsonData) {
      this.variantData = JSON.parse(this.jsonData.textContent);
    }
    return this.variantData || [];
  }
  
  onSubmit(event) {
    event.preventDefault();
    this.addToCart();
  }
  
  addToCart() {
    // Check if product is already in cart
    const cartItem = this.cartDrawer.querySelector(
      '.cart-item--product-' + this.handle
    );
    
    // Set button to loading state
    if (this.submitButton) {
      this.submitButton.classList.add('loading');
      this.submitButton.setAttribute('disabled', 'disabled');
      const spinner = this.submitButton.querySelector('.loading-overlay__spinner');
      if (spinner) spinner.classList.remove('hidden');
    }
    
    // Disable selects during operation
    if (this.selectElements) {
      this.selectElements.forEach(select => {
        select.setAttribute('disabled', 'disabled');
      });
    }
    
    // Add to cart via AJAX
    const formData = new FormData(this.form);
    
    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      body: formData
    })
    .then(response => response.json())
    .then(data => {
      // Update cart count
      this.updateCartCount();
      
      // Trigger cart update event to refresh cart items without full page reload
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'upsell-variant-selector'
        });
      } else {
        // Fallback: dispatch custom event if PUB_SUB_EVENTS not available
        document.dispatchEvent(new CustomEvent('cart:updated'));
      }
      
      // Reset button state
      if (this.submitButton) {
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('disabled');
        const spinner = this.submitButton.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.add('hidden');
      }
      
      // Re-enable selects
      if (this.selectElements) {
        this.selectElements.forEach(select => {
          select.removeAttribute('disabled');
        });
      }
    })
    .catch(error => {
      console.error('Error adding to cart:', error);
      
      // Reset button state
      if (this.submitButton) {
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('disabled');
        const spinner = this.submitButton.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.add('hidden');
      }
      
      // Re-enable selects
      if (this.selectElements) {
        this.selectElements.forEach(select => {
          select.removeAttribute('disabled');
        });
      }
    });
  }
  
  removeFromCart() {
    // Find the cart item
    const cartItem = this.cartDrawer.querySelector(
      '.cart-item--product-' + this.handle
    );
    
    if (cartItem && this.cartItems) {
      // Disable buttons during operation
      if (this.submitButton) {
        this.submitButton.setAttribute('disabled', 'disabled');
      }
      
      if (this.selectElements) {
        this.selectElements.forEach(select => {
          select.setAttribute('disabled', 'disabled');
        });
      }
      
      // Remove from cart
      this.cartItems.updateQuantity(cartItem.dataset.index, 0);
    }
  }
  
  updateCartCount() {
    fetch(window.Shopify.routes.root + 'cart.js')
      .then(response => response.json())
      .then(cart => {
        const cartCountElements = document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]');
        cartCountElements.forEach(el => {
          el.textContent = cart.item_count;
        });
        
        const cartCountAccessibleElements = document.querySelectorAll('.cart-count-bubble span:not([aria-hidden="true"])');
        cartCountAccessibleElements.forEach(el => {
          el.textContent = cart.item_count === 1 
            ? window.accessibilityStrings.cartSingularAccessibilityString 
            : window.accessibilityStrings.cartPluralAccessibilityString.replace('[count]', cart.item_count);
        });
      });
  }
}

customElements.define('cart-drawer-upsell-variant-selector', CartDrawerUpsellVariantSelector);
